import { FilterOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useMemo } from "react";
import { classNames } from "src/common/classNames";
import { PropsHeaderCell } from "src/components/ReactiveTable/components/ReactiveTable/componentProps";
import {
  AbstractFilters,
  AbstractRow,
} from "src/components/ReactiveTable/RtTypes";

import { FilterDropdown } from "../filters/FilterDropdown";
import { RtColumnResizerOptimized } from "../RtColumnResizerOptimized/RtColumnResizerOptimized";
import * as styles from "./HeaderCell.module.less";

// Простые SVG иконки для сортировки
const SortIconNeutral = ({ opacity = 0.3 }: { opacity?: number }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="currentColor"
    style={{ opacity }}
  >
    <path d="M6 1L3 4h6L6 1z" />
    <path d="M6 11l3-3H3l3 3z" />
  </svg>
);

const SortIconAscend = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 2L3 5h6L6 2z" />
  </svg>
);

const SortIconDescend = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 10l3-3H3l3 3z" />
  </svg>
);

/**
 * Ячейка заголовка таблицы
 *
 * Поддерживает:
 * - Сортировку (клик по заголовку)
 * - Фильтры (dropdown)
 * - Изменение размера (если resizable=true)
 */
export const HeaderCell = observer(
  <TRow extends AbstractRow, TFilters extends AbstractFilters>({
    column,
    store,
    columnFilters,
    resizable,
    style,
    className,
  }: PropsHeaderCell<TRow, TFilters>) => {
    const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false);
    const [containerElement, setContainerElement] =
      React.useState<HTMLDivElement | null>(null);
    // Callback ref для отслеживания монтирования элемента
    const cellRef = React.useCallback((node: HTMLDivElement | null) => {
      setContainerElement(node);
    }, []);

    // Получаем состояние сортировки для этой колонки
    const sortState = useMemo(() => {
      if (!column.sort) return null;

      const sortItem = store.sortOrder.find(
        (item) => item.columnKey === column.key,
      );

      if (!sortItem) return null;

      return {
        mode: sortItem.mode,
        priority:
          store.sortOrder.length > 1
            ? store.sortOrder.indexOf(sortItem) + 1
            : undefined,
      };
    }, [column.key, column.sort, store.sortOrder]);

    // Обработчик клика по заголовку (для сортировки)
    const handleClick = () => {
      if (column.sort) {
        store.onSortClick(column.key);
      }
    };

    // Получаем фильтр для этой колонки
    const filter = columnFilters?.[column.key];
    const filterValue = filter?.getValue?.();
    const hasActiveFilter = filterValue ? filterValue.length > 0 : false;
    const hasFilter = !!filter;

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        ref={cellRef}
        className={classNames([
          styles.headerCell,
          column.sort && styles.sortable,
          sortState && styles.sorted,
          hasActiveFilter && styles.filtered,
          className,
        ])}
        style={style}
        onClick={handleClick}
        data-column-key={column.key}
      >
        <div className={styles.headerContent}>
          {/* Заголовок */}
          <span className={styles.headerTitle}>{column.title}</span>

          {/* Иконка сортировки */}
          {column.sort && (
            <span className={styles.sortIcon}>
              {!sortState && <SortIconNeutral />}
              {sortState?.mode === "ascend" && <SortIconAscend />}
              {sortState?.mode === "descend" && <SortIconDescend />}
              {sortState?.priority && (
                <span className={styles.sortPriority}>
                  {sortState.priority}
                </span>
              )}
            </span>
          )}

          {/* Dropdown фильтр */}
          {hasFilter && (
            <Dropdown
              open={filterDropdownOpen}
              onOpenChange={setFilterDropdownOpen}
              trigger={["click"]}
              placement="bottomLeft"
              popupRender={() => (
                <FilterDropdown
                  column={column}
                  store={store}
                  columnFilters={columnFilters}
                  onClose={() => setFilterDropdownOpen(false)}
                />
              )}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <span
                className={classNames([
                  styles.filterTrigger,
                  hasActiveFilter && styles.filterActive,
                ])}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <FilterOutlined />
              </span>
            </Dropdown>
          )}
        </div>

        {/* Column Resizer */}
        {resizable && containerElement && (
          <RtColumnResizerOptimized
            column={column}
            store={store}
            container={containerElement}
          />
        )}
      </div>
    );
  },
);
